import { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button, Chip, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress, Alert, Tooltip, Collapse,
  IconButton, MenuItem, Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { apiService } from '../services/api';

const fmtPct = (v: any) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const fmtUsd = (v: any) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);

/** Colour by probability — nothing here reaches "good", so the scale tops out at amber. */
const popColor = (p: number) => (p >= 55 ? '#22C55E' : p >= 45 ? '#F59E0B' : '#EF4444');

// ---------------------------------------------------------------------------
// The probability-over-time curve, which is the whole point of the analyzer
// ---------------------------------------------------------------------------
function Surface({ surface, best }: { surface: any[]; best: any }) {
  if (!surface?.length) return <Typography variant="caption" color="text.disabled">No surface available</Typography>;
  const max = Math.max(...surface.map((s) => s.profit_probability_pct), 1);
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Probability of a profitable exit, by date — the peak is usually <b>not</b> expiry,
        because time value lets you sell at a profit before the stock reaches the strike.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Exit date</TableCell>
            <TableCell align="right">Held</TableCell>
            <TableCell align="right">Need spot</TableCell>
            <TableCell align="right">Move</TableCell>
            <TableCell align="right">P(profit)</TableCell>
            <TableCell width={120} />
          </TableRow>
        </TableHead>
        <TableBody>
          {surface.map((s) => {
            const isBest = best && s.exit_date === best.exit_date;
            return (
              <TableRow key={s.exit_date} sx={{ bgcolor: isBest ? '#22C55E14' : undefined }}>
                <TableCell>{s.exit_date}{isBest && <Chip size="small" label="best" sx={{ ml: 1, height: 18 }} />}</TableCell>
                <TableCell align="right">{s.days_held}d</TableCell>
                <TableCell align="right">${s.breakeven_spot}</TableCell>
                <TableCell align="right">{fmtPct(s.breakeven_move_pct)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: popColor(s.profit_probability_pct) }}>
                  {s.profit_probability_pct}%
                </TableCell>
                <TableCell>
                  <Box sx={{ height: 8, borderRadius: 1, bgcolor: '#8883',
                    width: `${(s.profit_probability_pct / max) * 100}%`,
                    backgroundColor: popColor(s.profit_probability_pct) }} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function ContractRow({ c }: { c: any }) {
  const [open, setOpen] = useState(false);
  const e = c.economics || {};
  const p = c.probability || {};
  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip size="small" label={c.type.toUpperCase()}
            sx={{ bgcolor: c.type === 'call' ? '#22C55E22' : '#EF444422',
                  color: c.type === 'call' ? '#22C55E' : '#EF4444', fontWeight: 700 }} />
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>${c.strike}</TableCell>
        <TableCell>{c.expiration}</TableCell>
        <TableCell align="right">{fmtUsd(e.gross_cost)}</TableCell>
        <TableCell align="right">
          <Tooltip title="How far the underlying must move for this position to clear its own cost, including fees and the spread you pay twice.">
            <span>{fmtPct(e.breakeven_move_pct)}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{c.spread_pct}%</TableCell>
        <TableCell align="right">{c.delta?.toFixed?.(2) ?? '—'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, color: popColor(p.pop) }}>{p.pop}%</TableCell>
        <TableCell align="right">
          {c.best_exit
            ? <Tooltip title={`Exiting on ${c.best_exit.exit_date} needs only ${c.best_exit.breakeven_move_pct}%`}>
                <span style={{ color: popColor(c.best_exit.profit_probability_pct), fontWeight: 700 }}>
                  {c.best_exit.profit_probability_pct}%
                </span>
              </Tooltip>
            : '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#8881', borderRadius: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Cost {fmtUsd(e.gross_cost)} = premium at the ask · commissions {fmtUsd(e.commissions)} ·
                spread cost {fmtUsd(e.spread_cost)} — total friction {fmtUsd(e.total_friction)}
                {' '}({e.friction_pct_of_budget}% of budget)
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                P(finishing in the money) {p.p_itm_model}% vs P(profit) {p.pop}% —
                the {p.cost_drag_pts}pt gap is what cost takes from you.
              </Typography>
              <Surface surface={c.probability_surface} best={c.best_exit} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Options() {
  const [tab, setTab] = useState(0);
  const [symbol, setSymbol] = useState('BAC');
  const [budget, setBudget] = useState(300);
  const [side, setSide] = useState('');
  const [data, setData] = useState<any>(null);
  const [recs, setRecs] = useState<any>(null);
  const [evidence, setEvidence] = useState<any>(null);
  const [recSymbols, setRecSymbols] = useState('BAC,F,SPY,PFE,T');
  const [scan, setScan] = useState<any>(null);
  const [scanSymbols, setScanSymbols] = useState('');
  const [minDte, setMinDte] = useState(60);
  const [maxDte, setMaxDte] = useState(120);
  const [target, setTarget] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (fn: () => Promise<any>, set: (v: any) => void) => {
    setLoading(true); setError(null);
    try { set(await fn()); }
    catch (e: any) { setError(e?.response?.data?.detail || e.message || 'Request failed'); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>Options</Typography>

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        <b>Backtested and failed.</b> Ranked selection returned a profit factor of 0.64 over 33
        trades and <b>lost to picking contracts at random</b> (0.78) — while 19 of 25 underlyings
        rose. The <b>Analyzer</b> tells you the truth about a contract you choose; the{' '}
        <b>Advisor</b> is a screen, not advice. See the Evidence tab.
      </Alert>

      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Scanner" />
        <Tab label="Analyzer" />
        <Tab label="Advisor (unvalidated)" />
        <Tab label="Evidence" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

{tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="Symbols (blank = watchlist)" value={scanSymbols}
              onChange={(e) => setScanSymbols(e.target.value.toUpperCase())} sx={{ width: 260 }} />
            <TextField size="small" label="Budget $" type="number" value={budget}
              onChange={(e) => setBudget(Number(e.target.value))} sx={{ width: 110 }} />
            <TextField size="small" label="Min days" type="number" value={minDte}
              onChange={(e) => setMinDte(Number(e.target.value))} sx={{ width: 100 }} />
            <TextField size="small" label="Max days" type="number" value={maxDte}
              onChange={(e) => setMaxDte(Number(e.target.value))} sx={{ width: 100 }} />
            <TextField size="small" label="Take profit %" type="number" value={target}
              onChange={(e) => setTarget(Number(e.target.value))} sx={{ width: 120 }} />
            <TextField size="small" select label="Side" value={side}
              onChange={(e) => setSide(e.target.value)} sx={{ width: 110 }}>
              <MenuItem value="">Both</MenuItem>
              <MenuItem value="call">Calls</MenuItem>
              <MenuItem value="put">Puts</MenuItem>
            </TextField>
            <Button variant="contained" disabled={loading}
              onClick={() => call(() => apiService.scanOptions({
                symbols: scanSymbols || undefined, budget, side: side || undefined,
                min_dte: minDte, max_dte: maxDte, profit_target_pct: target, limit: 40,
              }), setScan)}>
              {loading ? <CircularProgress size={20} /> : 'Scan'}
            </Button>
          </Box>

          {loading && <Typography variant="caption" color="text.secondary">
            Scanning live chains — this takes 20–60s for a full watchlist.
          </Typography>}

          {scan && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Scanned <b>{scan.scanned_symbols}</b> symbols · {scan.symbols_with_candidates} had
                tradeable contracts · {scan.count} candidates · horizon {scan.horizon_dte?.[0]}–{scan.horizon_dte?.[1]} days
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell align="right">Limit</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Spread</TableCell>
                    <TableCell align="right">Δ</TableCell>
                    <TableCell align="right">Breakeven</TableCell>
                    <TableCell align="right">P(hit target)</TableCell>
                    <TableCell align="right">Move needed</TableCell>
                    <TableCell align="right">Take profit</TableCell>
                    <TableCell>Context</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scan.candidates?.map((c: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{c.action}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {c.dte}d out · spot ${c.spot}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">${c.limit_price}
                        <Typography variant="caption" display="block" color="text.disabled">
                          max ${c.do_not_pay_above}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmtUsd(c.total_cost)}</TableCell>
                      <TableCell align="right" sx={{ color: c.spread_pct > 10 ? '#EF4444' : undefined }}>
                        {c.spread_pct}%
                      </TableCell>
                      <TableCell align="right">{c.delta?.toFixed?.(2)}</TableCell>
                      <TableCell align="right">{fmtPct(c.breakeven_move_pct)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700,
                        color: (c.prob_hit_target_pct ?? 0) >= 30 ? '#22C55E'
                          : (c.prob_hit_target_pct ?? 0) >= 20 ? '#F59E0B' : '#EF4444' }}>
                        <Tooltip title={`Chance this is up ${c.profit_target_pct}% by the date shown — the number that matches a "cash out once up X%" rule. Break-even probability is near 50% for everything and tells you nothing.`}>
                          <span>{c.prob_hit_target_pct ?? '—'}%</span>
                        </Tooltip>
                        {c.target_window && <Typography variant="caption" display="block" color="text.disabled">
                          @{c.target_window.days_held}d
                        </Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {c.target_window ? fmtPct(c.target_window.move_needed_pct) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#22C55E' }}>${c.take_profit_at}
                        <Typography variant="caption" display="block" color="text.disabled">
                          stop ${c.stop_loss_at}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={c.confidence?.level}
                          sx={{ height: 20,
                            bgcolor: c.confidence?.level === 'aligned' ? '#22C55E22'
                              : c.confidence?.level === 'conflicted' ? '#EF444422' : '#8882',
                            color: c.confidence?.level === 'aligned' ? '#22C55E'
                              : c.confidence?.level === 'conflicted' ? '#EF4444' : 'inherit' }} />
                        {c.confidence?.agrees?.length > 0 && (
                          <Typography variant="caption" display="block" color="text.disabled">
                            {c.confidence.agrees.join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Divider sx={{ my: 2 }} />
              {scan.how_to_read?.map((h: string, i: number) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">• {h}</Typography>
              ))}
            </>
          )}
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Symbol" value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())} sx={{ width: 120 }} />
            <TextField size="small" label="Budget $" type="number" value={budget}
              onChange={(e) => setBudget(Number(e.target.value))} sx={{ width: 120 }} />
            <TextField size="small" select label="Side" value={side}
              onChange={(e) => setSide(e.target.value)} sx={{ width: 120 }}>
              <MenuItem value="">Both</MenuItem>
              <MenuItem value="call">Calls</MenuItem>
              <MenuItem value="put">Puts</MenuItem>
            </TextField>
            <Button variant="contained" disabled={loading}
              onClick={() => call(() => apiService.analyzeOptions(symbol, budget, side || undefined), setData)}>
              {loading ? <CircularProgress size={20} /> : 'Analyze'}
            </Button>
          </Box>

          {data && (
            <>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="body2"><b>{data.symbol}</b> ${data.spot}</Typography>
                <Typography variant="body2" color="text.secondary">
                  IV {data.volatility?.iv} · HV {data.volatility?.hv} · IV rank {data.volatility?.iv_rank}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.chain_size} contracts → <b>{data.affordable_and_reachable}</b> affordable with a reachable breakeven
                </Typography>
              </Box>
              {data.data_source === 'live' && (
                <Alert severity="info" sx={{ mb: 1 }} icon={false}>
                  Live quotes from Yahoo — this symbol is not in the historical dataset.
                  Greeks are <b>computed from the quoted IV</b>, not supplied by the exchange.
                </Alert>
              )}
              {data.volatility?.iv && (
                <Alert severity="info" sx={{ mb: 2 }} icon={false}>{data.volatility?.reading}</Alert>
              )}

              {data.diagnostic && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <b>Nothing tradeable at this budget.</b><br />
                  {data.diagnostic.message}
                  {data.diagnostic.suggestion && (
                    <><br /><i>{data.diagnostic.suggestion}</i></>
                  )}
                </Alert>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Type</TableCell><TableCell>Strike</TableCell><TableCell>Expiry</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Breakeven</TableCell>
                    <TableCell align="right">Spread</TableCell>
                    <TableCell align="right">Δ</TableCell>
                    <TableCell align="right">POP @expiry</TableCell>
                    <TableCell align="right">Best exit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.contracts?.map((c: any, i: number) => <ContractRow key={i} c={c} />)}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />
              {data.caveats?.map((c: string, i: number) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">• {c}</Typography>
              ))}
            </>
          )}
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Symbols (comma-separated)" value={recSymbols}
              onChange={(e) => setRecSymbols(e.target.value.toUpperCase())} sx={{ width: 320 }} />
            <TextField size="small" label="Budget $" type="number" value={budget}
              onChange={(e) => setBudget(Number(e.target.value))} sx={{ width: 120 }} />
            <Button variant="contained" disabled={loading}
              onClick={() => call(() => apiService.getOptionRecommendations(recSymbols, budget, side || undefined), setRecs)}>
              {loading ? <CircularProgress size={20} /> : 'Rank'}
            </Button>
          </Box>

          {recs && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>{recs.warning?.summary}</Alert>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell><TableCell>Type</TableCell><TableCell>Strike</TableCell>
                    <TableCell>Expiry</TableCell><TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Breakeven</TableCell><TableCell align="right">Spread</TableCell>
                    <TableCell align="right">POP</TableCell><TableCell align="right">Best exit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recs.picks?.map((p: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{p.symbol}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.type.toUpperCase()}
                          sx={{ bgcolor: p.type === 'call' ? '#22C55E22' : '#EF444422',
                                color: p.type === 'call' ? '#22C55E' : '#EF4444' }} />
                      </TableCell>
                      <TableCell>${p.strike}</TableCell>
                      <TableCell>{p.expiration}</TableCell>
                      <TableCell align="right">{fmtUsd(p.cost)}</TableCell>
                      <TableCell align="right">{fmtPct(p.breakeven_move_pct)}</TableCell>
                      <TableCell align="right">{p.spread_pct}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: popColor(p.pop_at_expiry) }}>
                        {p.pop_at_expiry}%
                      </TableCell>
                      <TableCell align="right">
                        {p.best_exit ? `${p.best_exit.profit_probability_pct}% @ ${p.best_exit.days_held}d` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      )}

      {tab === 3 && (
        <Paper sx={{ p: 2 }}>
          <Button variant="outlined" sx={{ mb: 2 }} disabled={loading}
            onClick={() => call(() => apiService.getOptionsEvidence(), setEvidence)}>
            Load backtest evidence
          </Button>
          {evidence && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>{evidence.conclusion}</b><br />{evidence.why}
              </Alert>

              <Typography variant="h6" gutterBottom>Long-premium backtest</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {evidence.long_premium_backtest?.window} · {evidence.long_premium_backtest?.symbols} symbols ·
                ${evidence.long_premium_backtest?.budget} · {evidence.long_premium_backtest?.fills}
              </Typography>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Arm</TableCell><TableCell align="right">n</TableCell>
                    <TableCell align="right">Win rate</TableCell><TableCell align="right">Profit factor</TableCell>
                    <TableCell align="right">Net P&L</TableCell><TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evidence.long_premium_backtest?.arms?.map((a: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{a.arm}</TableCell>
                      <TableCell align="right">{a.n}</TableCell>
                      <TableCell align="right">{a.win_rate_pct}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: a.profit_factor >= 1 ? '#22C55E' : '#EF4444' }}>
                        {a.profit_factor}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#EF4444' }}>{fmtUsd(a.net_pnl)}</TableCell>
                      <TableCell><Typography variant="caption">{a.note}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Alert severity="info" sx={{ mb: 3 }} icon={false}>
                {evidence.long_premium_backtest?.context}
              </Alert>

              <Typography variant="h6" gutterBottom>Why credit spreads fail too</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{evidence.credit_spread_arithmetic?.finding}</Typography>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="right">Width</TableCell><TableCell align="right">Credit</TableCell>
                    <TableCell align="right">Risk</TableCell><TableCell align="right">Net reward</TableCell>
                    <TableCell align="right">Breakeven win rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evidence.credit_spread_arithmetic?.examples?.map((e: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell align="right">{e.width}</TableCell>
                      <TableCell align="right">${e.credit}</TableCell>
                      <TableCell align="right">${e.risk}</TableCell>
                      <TableCell align="right">${e.net_reward}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#EF4444' }}>
                        {e.breakeven_win_rate_pct}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                {evidence.credit_spread_arithmetic?.note}
              </Typography>

              <Typography variant="h6" gutterBottom>What would change the answer</Typography>
              {evidence.what_would_change_the_answer?.map((w: string, i: number) => (
                <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {w}</Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
